import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { Subscription, debounceTime, distinctUntilChanged, map, skip } from 'rxjs';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ImageService } from '@/app/services/image/image.service';
import { AdminCancellationsService } from '../../services/admin-cancellations.service';
import { CancellationDetailDrawer } from '../cancellation-detail-drawer/cancellation-detail-drawer';
import {
  formatMinorUnits,
  outcomeChipClass,
  outcomeLabel,
  scopeLabel,
} from '../../cancellations.helpers';
import {
  CancellationResponseRow,
  CancellationResponsesFilter,
  OffboardingOutcome,
  OffboardingScope,
} from '../../cancellations.types';

type LoadingState = 'idle' | 'loading' | 'loaded' | 'error' | 'forbidden';
type SortField = 'createdDate' | 'outcome' | 'reasonId' | 'renewalAmountMinorUnits';

/** Every outcome is offered, including the two that no legacy report exposed. */
const OUTCOME_FILTERS: { id: OffboardingOutcome; label: string }[] = [
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'kept', label: 'Saved' },
  { id: 'paused', label: 'Paused' },
  { id: 'started', label: 'Abandoned' },
  { id: 'reactivated', label: 'Reactivated' },
  { id: 'pause_cancelled', label: 'Pause cancelled' },
];

/**
 * The individual exits, and the dashboard's table view.
 *
 * Deep-linkable in both directions: the dashboard hands over its date range and
 * any filter it was showing, and opening a row writes `?responseId=` so a single
 * exit can be pasted to someone else.
 */
@Component({
  selector: 'admin-cancellations-responses',
  imports: [
    InfiniteScrollDirective,
    LdgImgComponent,
    LoadingInlineComponent,
    CancellationDetailDrawer,
    DatePipe,
  ],
  templateUrl: './cancellations-responses.html',
  styleUrl: './cancellations-responses.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CancellationsResponses implements OnDestroy {
  readonly rows = signal<CancellationResponseRow[]>([]);
  readonly totalCount = signal(0);
  readonly loadingState = signal<LoadingState>('idle');
  readonly loadingMore = signal(false);
  readonly error = signal<string | null>(null);
  readonly hasMore = signal(true);

  readonly searchQuery = signal('');
  readonly selectedOutcomes = signal<OffboardingOutcome[]>([]);
  readonly selectedReturn = signal<string | null>(null);
  readonly commentsOnly = signal(false);
  readonly sortField = signal<SortField>('createdDate');
  readonly sortDirection = signal<'ASC' | 'DESC'>('DESC');
  readonly openResponseId = signal<string | null>(null);

  readonly outcomeFilters = OUTCOME_FILTERS;
  readonly outcomeChipClass = outcomeChipClass;
  readonly outcomeLabel = outcomeLabel;
  readonly scopeLabel = scopeLabel;
  readonly imageService = inject(ImageService);

  readonly scrollDistance = 2;
  readonly throttle = 300;

  /** Refetch holds the current rows rather than flashing an empty table. */
  readonly isRefreshing = computed(
    () => this.loadingState() === 'loading' && this.rows().length > 0,
  );

  private readonly pageSize = 25;
  private skipCount = 0;
  private from?: string;
  private to?: string;
  private scope?: OffboardingScope;

  private fetchSubscription = new Subscription();
  private readonly searchSubscription = new Subscription();

  private readonly cancellationsService = inject(AdminCancellationsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  constructor() {
    const params = this.route.snapshot.queryParamMap;
    this.from = params.get('from') ?? undefined;
    this.to = params.get('to') ?? undefined;
    this.scope = (params.get('scope') as OffboardingScope | null) ?? undefined;
    this.selectedReturn.set(params.get('returnLikelihood'));
    this.openResponseId.set(params.get('responseId'));

    this.searchSubscription.add(
      toObservable(this.searchQuery)
        .pipe(
          map((query) => query.trim()),
          debounceTime(300),
          distinctUntilChanged(),
          skip(1),
        )
        .subscribe(() => this.load(true)),
    );

    this.load(true);
  }

  ngOnDestroy(): void {
    this.fetchSubscription.unsubscribe();
    this.searchSubscription.unsubscribe();
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
  }

  toggleOutcome(outcome: OffboardingOutcome): void {
    const current = this.selectedOutcomes();
    this.selectedOutcomes.set(
      current.includes(outcome)
        ? current.filter((entry) => entry !== outcome)
        : [...current, outcome],
    );
    this.load(true);
  }

  isOutcomeSelected(outcome: OffboardingOutcome): boolean {
    return this.selectedOutcomes().includes(outcome);
  }

  getFilterChipClass(active: boolean): string {
    return active
      ? 'border-violet-500 bg-violet-50 text-violet-700 dark:border-violet-400 dark:bg-violet-500/20 dark:text-violet-100'
      : 'border-zinc-200 text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20';
  }

  toggleCommentsOnly(): void {
    this.commentsOnly.update((value) => !value);
    this.load(true);
  }

  clearReturnFilter(): void {
    this.selectedReturn.set(null);
    this.load(true);
  }

  toggleSort(field: SortField): void {
    if (this.sortField() === field) {
      this.sortDirection.update((direction) => (direction === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      this.sortField.set(field);
      this.sortDirection.set('DESC');
    }
    this.load(true);
  }

  isSortedBy(field: SortField): boolean {
    return this.sortField() === field;
  }

  onScrollDown(): void {
    if (!this.hasMore() || this.loadingMore() || this.loadingState() === 'loading') return;
    this.load(false);
  }

  openResponse(id: string): void {
    this.openResponseId.set(id);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { responseId: id },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  closeResponse(): void {
    this.openResponseId.set(null);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { responseId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  money(row: CancellationResponseRow): string {
    return formatMinorUnits(row.monthlyValueMinorUnits, row.currencyCode);
  }

  private buildFilter(reset: boolean): CancellationResponsesFilter {
    const returnLikelihood = this.selectedReturn();
    const searchTerm = this.searchQuery().trim();
    const outcomes = this.selectedOutcomes();

    return {
      ...(this.from ? { from: this.from } : {}),
      ...(this.to ? { to: this.to } : {}),
      ...(this.scope ? { scope: this.scope } : {}),
      ...(outcomes.length ? { outcomes } : {}),
      ...(returnLikelihood ? { returnLikelihoods: [returnLikelihood] } : {}),
      ...(this.commentsOnly() ? { hasComment: true } : {}),
      ...(searchTerm ? { searchTerm } : {}),
      skip: reset ? 0 : this.skipCount,
      take: this.pageSize,
      sortBy: this.sortField(),
      sortDirection: this.sortDirection(),
    };
  }

  private load(reset: boolean): void {
    if (reset) {
      this.fetchSubscription.unsubscribe();
      this.fetchSubscription = new Subscription();
      this.skipCount = 0;
      this.hasMore.set(true);
      this.loadingState.set('loading');
    } else {
      this.loadingMore.set(true);
    }

    this.error.set(null);

    this.fetchSubscription.add(
      this.cancellationsService.listResponses(this.buildFilter(reset)).subscribe({
        next: (page) => {
          this.rows.set(reset ? page.rows : [...this.rows(), ...page.rows]);
          this.totalCount.set(page.totalCount);
          this.skipCount = this.rows().length;
          this.hasMore.set(this.rows().length < page.totalCount);
          this.loadingState.set('loaded');
          this.loadingMore.set(false);
        },
        error: (requestError: HttpErrorResponse) => {
          this.loadingMore.set(false);
          if (requestError.status === 403) {
            this.loadingState.set('forbidden');
            return;
          }
          this.error.set(
            requestError.error?.message ?? 'Could not load cancellation responses',
          );
          this.loadingState.set('error');
        },
      }),
    );
  }
}
